import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { NumericFormat } from 'react-number-format'

import Checkbox from '../../../../../../components/form/Checkbox'

import { CurrencyList, Okei } from '../../../../Plans/Positions/Edit/types'
import Okpd2Dropdown from '../../../../Plans/Positions/Edit/components/Okpd2Dropdown'

import MoneyInput from '../../../../../../components/form/MoneyInput'
import LoadingIcon from '../../../../../../components/LoadingIcon'
import {
    CountryList,
    CountryOfOrigin,
    CountryManufacturer,
    Contract,
    ContractPosition,
    Country,
    NationalReestrType,
    BudgetResources,
} from '../../../types'

import { OrganizationInfo } from '../../../../Org/Home/types'

import { Okpd2 } from '../../../../Org/Handbook/types'

import HoverTooltip from '../../../../../../components/HoverTooltip'
import MultilineSelect from '../../../../../../components/form/MultilineSelect'

import ContractPositionCountryOriginPopup from './ContractPositionCountryOriginPopup'
import ContractPositionCountryManufacturerPopup from './ContractPositionCountryManufacturerPopup'

import { ContractPositionRowError } from '../types'

type Props = {
    theme: string
    contractPositionGuid: string
    contractPosition: ContractPosition
    handleContractPositionEditPopup: () => void
    handleContractPositionCountryOriginPopup: () => void
    handleContractPositionCountryManufacturerPopup: () => void
    okeiList: Okei[]
    onSave: (patch: Partial<ContractPosition>) => void
    orgData: OrganizationInfo | undefined
    errors?: ContractPositionRowError
    contractAction: boolean
}

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-' // Обрабатываем пустые значения

    const parsedNum = typeof num === 'number' ? num : parseFloat(num)

    if (isNaN(parsedNum)) {
        return '-' // Обрабатываем некорректные значения
    }

    return parsedNum
        .toFixed(4) // Два знака после запятой
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') // Разделяем тысячные пробелами
        .replace('.', ',') // Заменяем точку на запятую
}

const ContractPositionEditPopup: React.FC<Props> = (props) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    const [form, setForm] = useState<ContractPosition>(props.contractPosition)

    const [
        contractPositionCountryOriginPopup,
        setContractPositionCountryOriginPopup,
    ] = useState<boolean>(false)
    const [
        contractPositionCountryManufacturerPopup,
        setContractPositionCountryManufacturerPopup,
    ] = useState<boolean>(false)

    const budgetResourceOptions = Object.entries(BudgetResources).map(
        ([key, label]) => ({
            value: key,
            label,
        })
    )

    const [expenseTypes, setExpenseTypes] = useState<any[]>([])
    const [filteredExpenseTypes, setFilteredExpenseTypes] = useState<any[]>([])
    const [dropdownVisibleIndex, setDropdownVisibleIndex] = useState<
        number | null
    >(null)
    const [targetName, setTargetName] = useState<string>()
    const [typeCodeName, setTypeCodeName] = useState<string>()

    const [okpd2Open, setOkpd2Open] = useState(false)
    const [okpd2Items, setOkpd2Items] = useState<Okpd2[]>([])

    const okpd2DebRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [okpd2Query, setOkpd2Query] = useState(form.okpd2_code ?? '')

    const okpd2InputRef = useRef<HTMLInputElement>(null)
    const okpd2PrevRef = useRef<string>('') // что было до открытия
    const okpd2PickedRef = useRef<boolean>(false) // выбрали ли пункт

    const setField = <K extends keyof ContractPosition>(
        key: K,
        value: ContractPosition[K]
    ) => {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }))
    }

    const handleContractPositionCountryOriginPopup = () => {
        setContractPositionCountryOriginPopup(
            !contractPositionCountryOriginPopup
        )
    }

    const handleContractPositionCountryManufacturerPopup = () => {
        setContractPositionCountryManufacturerPopup(
            !contractPositionCountryManufacturerPopup
        )
    }

    // Получение наименования для `target_code`
    const fetchTargetName = async (targetCode: string): Promise<string> => {
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v1/financial_provision_target/code_search?text_query=${targetCode.substring(
                        3,
                        5
                    )}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (!response.ok) {
                throw new Error(`Ошибка HTTP: ${response.status}`)
            }

            const data = await response.json()
            return data[0]?.name || 'Не найдено'
        } catch (error) {
            console.error('Ошибка при загрузке target_name:', error)
            return 'Ошибка загрузки'
        }
    }

    // Получение всех видов расходов
    const fetchAllExpenseTypes = async (): Promise<
        { guid: string; code: string; name: string }[]
    > => {
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    '/api/backend/v1/financial_provision_type',
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (!response.ok) {
                throw new Error(`Ошибка HTTP: ${response.status}`)
            }

            const data = await response.json()
            setExpenseTypes(data)
            setFilteredExpenseTypes(data)
            return data
        } catch (error) {
            console.error('Ошибка при загрузке видов расходов:', error)
            return [] // Возвращаем пустой массив в случае ошибки
        }
    }

    // Поиск видов расходов
    const searchExpenseTypes = async (query: string) => {
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v1/financial_provision_type/search?text_query=${query}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (!response.ok) {
                throw new Error(`Ошибка HTTP: ${response.status}`)
            }

            const data = await response.json()
            setFilteredExpenseTypes(data)
            return data
        } catch (error) {
            console.error('Ошибка при поиске видов расходов:', error)
        }
    }

    // Обновление `target_code` и автоматическое заполнение `target_name`
    const handleTargetCodeChange = async (value: string) => {
        setField('target_code', value)

        // Если заполнены 5 и 6 символы, запускаем запрос
        if (value.length >= 10) {
            const tn = await fetchTargetName(value)
            setTargetName(tn)
        } else {
            setTargetName('') // Очищаем, если код не полный
        }
    }

    // Автозаполнение данных при загрузке
    const autoFillItems = async () => {
        try {
            // Шаг 1: Загружаем все виды расходов (КВР)
            const fetchedExpenseTypes = await fetchAllExpenseTypes()

            // ВАЖНО: если NO_BUDGET_RESOURCES_FZ44 — то все,
            if (form.type_provision == 'NO_BUDGET_RESOURCES_FZ44') return

            if (form.target_code) {
                const tn = await fetchTargetName(form.target_code)
                if (tn) {
                    setTargetName(tn)
                }
            }
            if (form.type_code) {
                const tcn = await searchExpenseTypes(String(form.type_code))
                if (tcn) {
                    setTypeCodeName(tcn[0].name)
                }
            }
        } catch (error) {
            console.error('Ошибка при автозаполнении данных:', error)
        }
    }

    useEffect(() => {
        autoFillItems()
    }, [])

    const fetchOkpd2 = async (q: string) => {
        if (!q?.trim()) {
            setOkpd2Items([])
            return
        }
        const isCode = /^[0-9]/.test(q.trim())
        const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/okpd2/${isCode ? 'search_code' : 'search_name'}?text_query=${encodeURIComponent(q.trim())}`
        const res = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        })
        if (!res.ok) return
        const data: Okpd2[] = await res.json()
        setOkpd2Items(data)
    }

    const onOkpd2Input = (v: string) => {
        setOkpd2Query(v)
        if (okpd2DebRef.current) clearTimeout(okpd2DebRef.current)
        okpd2DebRef.current = setTimeout(() => {
            fetchOkpd2(v)
            setOkpd2Open(true)
        }, 500)
    }

    const errorClass = (hasError?: boolean): string => {
        return hasError ? 'border border-[#C15C5C]' : ''
    }

    return (
        <div className="fixed z-10 top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center">
            <div
                className="flex flex-col p-5 rounded-lg w-[880px] h-fit gap-y-5 bg-[#e5e5e5] dark:bg-[#262427]"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[16px]">
                    Редактирование позиции договора
                </h2>

                <div className="flex flex-col w-full gap-3">
                    <div className="flex flex-1 items-center justify-start text-[#181818] dark:text-[#FFFFFF] ">
                        <div className="flex flex-col w-full">
                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Наименование товаров, работ, услуг
                            </label>
                            <textarea
                                ref={textareaRef}
                                className={`form-textarea flex min-h-[26px] w-full p-1 px-2 rounded-md text-[11px] text-[#181818] dark:text-[#FFFFFF] bg-[#181818] ${errorClass(
                                    !!props.errors?.contract_position_name
                                )}`}
                                maxLength={2000}
                                value={form.contract_position_name ?? ''}
                                rows={2}
                                onChange={(e) =>
                                    setField(
                                        'contract_position_name',
                                        e.target.value
                                    )
                                }
                                disabled={props.contractAction}
                            />
                        </div>
                    </div>
                    <div className="flex flex-row w-full gap-3">
                        <div className="flex flex-col w-[200px]">
                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Тип объекта закупки
                            </label>
                            <select
                                name="purchase_item_type_purchase"
                                className={`form-input px-2 rounded-md bg-[#181818] w-full ${errorClass(
                                    !!props.errors?.type_object_purchase
                                )}`}
                                value={form.type_object_purchase ?? ''}
                                onChange={(e) =>
                                    setField(
                                        'type_object_purchase',
                                        e.target.value
                                    )
                                }
                                disabled={props.contractAction}
                            >
                                <option
                                    className="text-[#6A6A6A] dark:text-[#8A8A8A]"
                                    value=""
                                >
                                    Выбрать
                                </option>
                                <option value="G">Товар</option>
                                <option value="W">Работа</option>
                                <option value="S">Услуга</option>
                            </select>
                        </div>
                        <div className="flex flex-col flex-1 w-full">
                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                ОКПД2
                            </label>
                            <div className="flex w-full items-center justify-end">
                                <div className="flex flex-row w-full gap-2 text-[#181818] dark:text-[#FFFFFF]">
                                    <div className="relative flex flex-row w-[150px] items-center justify-end">
                                        <input
                                            ref={okpd2InputRef}
                                            data-autocomplete="okpd2"
                                            type="text"
                                            className={`form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                                !!props.errors?.okpd2_code
                                            )}`}
                                            value={okpd2Query}
                                            onChange={(e) => {
                                                okpd2PickedRef.current = false
                                                onOkpd2Input(
                                                    e.target.value.replace(
                                                        ',',
                                                        '.'
                                                    )
                                                )
                                            }}
                                            onFocus={() => {
                                                okpd2PrevRef.current =
                                                    okpd2Query
                                                okpd2PickedRef.current = false
                                                if (okpd2Items.length)
                                                    setOkpd2Open(true)
                                            }}
                                            disabled={props.contractAction}
                                        />
                                        <Okpd2Dropdown
                                            anchorEl={okpd2InputRef.current}
                                            open={okpd2Open}
                                            onClose={() => {
                                                setOkpd2Open(false)
                                                if (!okpd2PickedRef.current) {
                                                    setOkpd2Query(
                                                        okpd2PrevRef.current
                                                    )
                                                }
                                            }}
                                            items={okpd2Items}
                                            theme={props.theme}
                                            onPick={(it) => {
                                                // ← сюда придёт выбранный элемент
                                                okpd2PickedRef.current = true
                                                okpd2PrevRef.current = it.code
                                                setOkpd2Open(false)
                                                setOkpd2Query(it.code)
                                                setField('okpd2_code', it.code)
                                                setForm((prev) => ({
                                                    ...prev,
                                                    okpd2_code: it.code,
                                                    okpd2_name: it.name,
                                                }))
                                            }}
                                            posMSPStatus={false}
                                            purchaseMethodCode={0}
                                            isFirstSelected={false}
                                            rowsCount={1}
                                            orgDataIsComplianceSmb={false}
                                            orgDataIgnoreMspOkpd2={true}
                                            orgDataAllOkpd2={true}
                                        />
                                        {/* <button className="absolute flex w-6 h-6 mr-1 rounded-full items-center justify-center hover:bg-[#e4e6e8] dark:hover:bg-[#656365]">
                                            <svg
                                                className="w-3 h-3"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                id="bars"
                                            >
                                                <path
                                                    fill={
                                                        props.theme == 'dark'
                                                            ? '#FFFFFF'
                                                            : '#181818'
                                                    }
                                                    d="M3,8H21a1,1,0,0,0,0-2H3A1,1,0,0,0,3,8Zm18,8H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Zm0-5H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
                                                ></path>
                                            </svg>
                                        </button> */}
                                    </div>
                                    <HoverTooltip
                                        tooltipText={
                                            form.okpd2_name != null
                                                ? String(
                                                      form.okpd2_code +
                                                          ' - ' +
                                                          form.okpd2_name
                                                  )
                                                : '-'
                                        }
                                        valign="bottom"
                                    >
                                        <input
                                            type="text"
                                            className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-sm text-[#181818] dark:text-[#FFFFFF] bg-[#FFFFFF] dark:bg-[#181818] focus:outline-none focus:ring-0"
                                            value={form.okpd2_name}
                                            disabled={true}
                                            readOnly
                                        />
                                    </HoverTooltip>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-row w-full gap-3">
                        <div className="flex flex-col w-[200px]">
                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Количество (объем)
                            </label>
                            <NumericFormat
                                value={form.qty ?? ''}
                                // важное:
                                decimalSeparator=","
                                thousandSeparator=" "
                                allowNegative={false}
                                allowLeadingZeros={false}
                                // максимум 4 знака после запятой
                                decimalScale={4}
                                // при пустом значении не принудительно не добиваем нули
                                fixedDecimalScale={false}
                                // это заставит onValueChange.value быть С ТОЧКОЙ (канонично для бэка)
                                valueIsNumericString
                                // ввод из буфера с запятой/точкой работает:
                                onValueChange={(v) => {
                                    setField(
                                        'qty',
                                        v.value === '' ? null : Number(v.value)
                                    )
                                }}
                                // не даём вводить ничего кроме цифр, точки, запятой
                                isAllowed={(vals) => {
                                    // react-number-format сам ограничит по decimalScale,
                                    // но добавим запрет на NaN
                                    if (vals.formattedValue === '') return true
                                    return !Number.isNaN(vals.floatValue)
                                }}
                                className={`form-input rounded-md text-primary text-left w-full px-2 ${errorClass(
                                    !!props.errors?.qty
                                )}`}
                                // Запрет стандартной прокрутки значения колесом
                                disabled={
                                    form.impossible_to_determine_attr ||
                                    props.contractAction
                                }
                            />
                        </div>
                        <div className="flex flex-col w-[200px]">
                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Единица измерения
                            </label>
                            <select
                                className={`form-input px-2 rounded-md w-full ${errorClass(
                                    !!props.errors?.okei_code
                                )}`}
                                value={form.okei_code ?? ''}
                                onChange={(e) => {
                                    const selectedCode = e.target.value
                                    const selectedOkei = props.okeiList.find(
                                        (okei) => okei.code === selectedCode
                                    )

                                    setForm((prev) => ({
                                        ...prev,
                                        okei_code: selectedOkei?.code ?? null,
                                        okei_name: selectedOkei?.name ?? null,
                                    }))
                                }}
                                disabled={
                                    form.impossible_to_determine_attr ||
                                    props.contractAction
                                }
                            >
                                {form.impossible_to_determine_attr ? (
                                    <option
                                        className="text-[#6A6A6A] dark:text-[#8A8A8A]"
                                        value=""
                                    ></option> // <-- пусто, когда impossible_to_determine_attr true (и в других твоих кейсах)
                                ) : (
                                    <option
                                        className="text-[#6A6A6A] dark:text-[#8A8A8A]"
                                        value=""
                                    >
                                        {form.okei_code == null
                                            ? 'Выбрать'
                                            : ''}
                                    </option> // <-- обычный плейсхолдер
                                )}
                                {props.okeiList.map((item, index) => (
                                    <option key={index} value={item.code}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex flex-col min-w-[200px]">
                        <Checkbox
                            label="Невозможно определить количество (объем)"
                            checked={form.impossible_to_determine_attr ?? false}
                            onChange={(checked) => {
                                setForm((prev) => ({
                                    ...prev,
                                    impossible_to_determine_attr: checked,
                                    qty: checked ? null : prev.qty,
                                    okei_code: checked ? null : prev.okei_code,
                                    okei_name: checked ? null : prev.okei_name,
                                }))
                            }}
                            disabled={props.contractAction}
                        />
                    </div>

                    <div className="flex flex-row w-full gap-3">
                        <div className="flex flex-col w-[200px]">
                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Цена за единицу
                            </label>
                            <NumericFormat
                                value={formatNumber(form.unit_price)}
                                className={`form-input rounded-md text-primary w-full px-2 text-right ${errorClass(
                                    !!props.errors?.unit_price
                                )}`}
                                allowNegative={false}
                                decimalScale={2}
                                fixedDecimalScale={true}
                                decimalSeparator=","
                                thousandSeparator=" "
                                placeholder="0,00"
                                disabled={props.contractAction}
                                onValueChange={(v) => {
                                    setField(
                                        'unit_price',
                                        v.value === '' ? null : Number(v.value)
                                    )
                                }}
                            />
                        </div>
                        <div className="flex flex-col w-[200px]">
                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Валюта
                            </label>
                            <input
                                type="text"
                                className="form-input flex pl-2 pr-9 w-full h-[28px] rounded-md text-[#181818] dark:text-[#FFFFFF] ..."
                                value={form.currency_name ?? ''}
                                disabled={true}
                            />
                        </div>
                        {form.currency_digital_code != 643 && (
                            <>
                                <div className="flex flex-col w-[200px]">
                                    <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Курс валюты
                                    </label>
                                    <NumericFormat
                                        value={formatNumber(form.exchange_rate)}
                                        className={`form-input rounded-md text-primary w-full px-2 text-right`}
                                        allowNegative={false}
                                        decimalScale={4}
                                        fixedDecimalScale={true}
                                        decimalSeparator=","
                                        thousandSeparator=" "
                                        disabled={true}
                                        onValueChange={(v) => {
                                            const dotString = v.value ?? ''
                                        }}
                                    />
                                </div>
                                <div className="flex flex-col w-[200px]">
                                    <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Цена за единицу (руб.)
                                    </label>
                                    <NumericFormat
                                        value={formatNumber(
                                            form.rub_unit_price
                                        )}
                                        className={`form-input rounded-md text-primary w-full px-2 text-right`}
                                        allowNegative={false}
                                        decimalScale={2}
                                        fixedDecimalScale={true}
                                        decimalSeparator=","
                                        thousandSeparator=" "
                                        disabled={true}
                                        onValueChange={(v) => {
                                            const dotString = v.value ?? ''
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex flex-row w-full gap-3">
                        <div className="flex flex-col w-[300px]">
                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Страна происхождения товара
                            </label>
                            <div
                                className={`relative flex w-[300px] text-[#181818] dark:text-[#FFFFFF] items-center justify-end`}
                            >
                                <HoverTooltip
                                    tooltipContent={
                                        form.country_of_origin != null
                                            ? form.country_of_origin.country.map(
                                                  (c, index) => (
                                                      <div key={index}>
                                                          {c.name}
                                                      </div>
                                                  )
                                              )
                                            : '-'
                                    }
                                    valign="bottom"
                                >
                                    <input
                                        type="text"
                                        className={`form-input flex pl-2 pr-9 w-full rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                            !!props.errors?.country_of_origin
                                        )}`}
                                        value={
                                            form.country_of_origin
                                                ? form.country_of_origin
                                                      ?.country[0].name +
                                                  ` ${form.country_of_origin.country.length > 1 ? `(${form.country_of_origin.country.length})` : ''}`
                                                : ''
                                        }
                                        disabled={props.contractAction}
                                        readOnly
                                    />
                                </HoverTooltip>
                                <button
                                    className="absolute flex w-6 h-6 mr-1 rounded-full items-center justify-center hover:bg-[#e4e6e8] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleContractPositionCountryOriginPopup
                                    }
                                    disabled={props.contractAction}
                                >
                                    {/* иконка */}
                                    <svg
                                        className="w-3 h-3"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="bars"
                                    >
                                        <path
                                            fill={
                                                props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M3,8H21a1,1,0,0,0,0-2H3A1,1,0,0,0,3,8Zm18,8H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Zm0-5H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
                                        ></path>
                                    </svg>
                                </button>
                                {form.country_of_origin ? (
                                    form.country_of_origin.country.length >
                                    1 ? (
                                        <div className="absolute right-[-4px] top-[-7px] flex w-4 h-4 items-center justify-center text-center rounded-full bg-[#494849]">
                                            {
                                                form.country_of_origin.country
                                                    .length
                                            }
                                        </div>
                                    ) : (
                                        ''
                                    )
                                ) : (
                                    ''
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col w-[300px]">
                            <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Страна регистрации производителя товара
                            </label>
                            <div className="relative flex w-[300px] text-[#181818] dark:text-[#FFFFFF] items-center justify-end">
                                <HoverTooltip
                                    tooltipText={
                                        form.country_manufacturer != null
                                            ? String(
                                                  form.country_manufacturer.name
                                              )
                                            : '-'
                                    }
                                    valign="bottom"
                                >
                                    <input
                                        type="text"
                                        className={`form-input flex pl-2 pr-9 w-full rounded-md text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                            !!props.errors?.country_manufacturer
                                        )}`}
                                        value={form.country_manufacturer?.name}
                                        disabled={props.contractAction}
                                        readOnly
                                    />
                                </HoverTooltip>
                                <button
                                    className="absolute flex w-6 h-6 mr-1 rounded-full items-center justify-center hover:bg-[#e4e6e8] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleContractPositionCountryManufacturerPopup
                                    }
                                    disabled={props.contractAction}
                                >
                                    {/* иконка */}
                                    <svg
                                        className="w-3 h-3"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        id="bars"
                                    >
                                        <path
                                            fill={
                                                props.theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d="M3,8H21a1,1,0,0,0,0-2H3A1,1,0,0,0,3,8Zm18,8H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Zm0-5H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
                                        ></path>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col min-w-[200px]">
                        <Checkbox
                            label="При осуществлении закупки был установлен запрет или ограничение закупок товаров, происходящих из иностранных государств, в соответствии с ПП РФ № 1875 от 23 декабря 2024 г."
                            checked={form.lot_item_national_regime ?? false}
                            onChange={(checked) => {
                                setForm((prev) => ({
                                    ...prev,
                                    lot_item_national_regime: checked,
                                    registry_type: checked
                                        ? prev.registry_type
                                        : null,
                                    reg_number: checked
                                        ? prev.reg_number
                                        : null,
                                    score: checked ? prev.score : null,
                                }))
                            }}
                            disabled={props.contractAction}
                        />
                    </div>

                    {form.lot_item_national_regime == true ? (
                        <>
                            <div className="flex flex-col">
                                <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    Тип реестра
                                </label>
                                <select
                                    className={`form-input w-full ${errorClass(
                                        !!props.errors?.registry_type
                                    )}`}
                                    value={form.registry_type ?? ''}
                                    onChange={(e) => {
                                        const value = e.target.value || null
                                        setForm((prev) => ({
                                            ...prev,
                                            registry_type: value,
                                            score:
                                                value === 'NSI_NP_RRPP' ||
                                                value === 'NSI_NP_ERPP'
                                                    ? prev.score
                                                    : null,
                                        }))
                                    }}
                                    disabled={props.contractAction}
                                >
                                    <option value="">
                                        Выберите тип реестра
                                    </option>

                                    {Object.entries(NationalReestrType).map(
                                        ([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>
                            <div className="flex flex-row w-full gap-5 ">
                                <div className="flex flex-col min-w-[200px]">
                                    <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                        Номер реестровой записи
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-input user-input-placeholder w-full ${errorClass(
                                            !!props.errors?.reg_number
                                        )}`}
                                        value={form.reg_number ?? ''}
                                        onChange={(e) =>
                                            setField(
                                                'reg_number',
                                                e.target.value || null
                                            )
                                        }
                                        style={{
                                            colorScheme:
                                                props.theme == 'dark'
                                                    ? 'dark'
                                                    : 'light',
                                        }}
                                        disabled={props.contractAction}
                                    />
                                </div>
                                {form.registry_type == 'NSI_NP_RRPP' ||
                                form.registry_type == 'NSI_NP_ERPP' ? (
                                    <div className="flex flex-col min-w-[200px]">
                                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Совокупное количество баллов
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-input user-input-placeholder w-full ${errorClass(
                                                !!props.errors?.score
                                            )}`}
                                            value={form.score ?? ''}
                                            onChange={(e) =>
                                                setField(
                                                    'score',
                                                    e.target.value === ''
                                                        ? null
                                                        : Number(e.target.value)
                                                )
                                            }
                                            style={{
                                                colorScheme:
                                                    props.theme == 'dark'
                                                        ? 'dark'
                                                        : 'light',
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                ) : (
                                    ''
                                )}
                            </div>
                        </>
                    ) : (
                        ''
                    )}
                    <div className="flex flex-col">
                        <label className="w-full text-sm text-[#6A6A6A] dark:text-[#8A8A8A]">
                            Финансовое обеспечение за счёт бюджетных средств
                        </label>
                        <MultilineSelect
                            isDisabled={props.contractAction}
                            options={budgetResourceOptions}
                            value={
                                budgetResourceOptions.find(
                                    (option) =>
                                        option.value === form.type_provision
                                ) ?? undefined
                            }
                            onChange={(newValue) => {
                                const value = newValue
                                if (value == 'NO_BUDGET_RESOURCES_FZ44') {
                                    setTargetName('')
                                }
                                setForm((prev) => ({
                                    ...prev,
                                    type_provision: value,
                                    target_code:
                                        value == 'NO_BUDGET_RESOURCES_FZ44'
                                            ? null
                                            : prev.target_code,
                                    type_code:
                                        value == 'NO_BUDGET_RESOURCES_FZ44'
                                            ? null
                                            : prev.type_code,
                                    amount_of_budget_funds:
                                        value == 'NO_BUDGET_RESOURCES_FZ44'
                                            ? null
                                            : prev.amount_of_budget_funds,
                                }))
                            }}
                            placeholder="Выбрать"
                            noOptionsMessage="Данные отсутствуют"
                        />
                    </div>
                    {form.type_provision ==
                        'NATIONAL_FEDERAL_PROJECTS_SUBSIDIES' && (
                        <div
                            className={`flex flex-col w-full min-w-fit rounded`}
                        >
                            {/* Заголовок таблицы */}
                            <div className="flex flex-row gap-1 h-10 text-[10px] p-1 rounded-t items-center text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2A282B]">
                                <HoverTooltip tooltipText="Код целевой статьи расходов">
                                    <div className="flex w-28 items-center justify-center text-center">
                                        Код ЦСР
                                    </div>
                                </HoverTooltip>
                                <div className="flex flex-1 items-center justify-center text-center">
                                    Наименование федерального проекта
                                </div>
                                <div className="flex flex-1 items-center justify-center text-center">
                                    Код вида расходов (КВР)
                                </div>
                                <div className="flex w-40 items-center justify-center text-center">
                                    Объем финансового обеспечения (руб.)
                                </div>
                            </div>
                            <div className="flex flex-col w-full items-center rounded-b bg-[#e4e6e8] dark:bg-[#242225]">
                                {/* Строки таблицы */}

                                <div className="flex flex-row gap-1 my-1 text-[11px] h-7 w-full px-1 items-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                    <input
                                        type="text"
                                        value={form.target_code ?? ''}
                                        onChange={(e) =>
                                            handleTargetCodeChange(
                                                e.target.value
                                            )
                                        }
                                        className={`form-input flex px-2 w-28 h-full rounded-md ${errorClass(
                                            !!props.errors?.target_code
                                        )}`}
                                        placeholder="Введите код"
                                        disabled={props.contractAction}
                                    />

                                    <HoverTooltip tooltipText={targetName}>
                                        <div className="flex flex-1 h-full">
                                            <input
                                                type="text"
                                                value={targetName}
                                                className={`form-input flex flex-1 px-2 w-full h-full rounded-md`}
                                                disabled
                                                placeholder="Наименование"
                                            />
                                        </div>
                                    </HoverTooltip>
                                    <div className="relative w-full flex-1 h-full">
                                        <HoverTooltip
                                            tooltipText={
                                                form.type_code
                                                    ? form.type_code +
                                                      ' - ' +
                                                      typeCodeName
                                                    : ''
                                            }
                                        >
                                            <input
                                                type="text"
                                                className={`form-input flex px-2 w-full h-full rounded-md ${errorClass(
                                                    !!props.errors?.type_code
                                                )}`}
                                                placeholder="Введите или выберите код вида расходов (КВР)"
                                                onFocus={() =>
                                                    setDropdownVisibleIndex(1)
                                                }
                                                onChange={(e) => {
                                                    const inputValue =
                                                        e.target.value
                                                    const [code] =
                                                        inputValue.split(' - ') // Извлекаем только код, если пользователь вводит полный формат

                                                    setTypeCodeName('')
                                                    setField(
                                                        'type_code',
                                                        Number(code)
                                                    )
                                                    // Выполняем запрос для фильтрации
                                                    if (code) {
                                                        searchExpenseTypes(code)
                                                    }
                                                }}
                                                value={
                                                    // form.type_code
                                                    //     ? form.type_code
                                                    //     : ''
                                                    form.type_code &&
                                                    typeCodeName
                                                        ? `${form.type_code} - ${typeCodeName}`
                                                        : form.type_code || ''
                                                }
                                                onBlur={() =>
                                                    setTimeout(
                                                        () =>
                                                            setDropdownVisibleIndex(
                                                                null
                                                            ),
                                                        100
                                                    )
                                                } // Задержка закрытия
                                                disabled={props.contractAction}
                                            />
                                        </HoverTooltip>
                                        {dropdownVisibleIndex === 1 && ( // Показываем список только для текущей строки
                                            <div className="absolute z-50 w-full h-52 rounded shadow-md overflow-y-auto border border-[#c4c4c4] dark:border-[#75747A] bg-[#FFFFFF] dark:bg-[#181818] custom-scrollbar">
                                                {filteredExpenseTypes.map(
                                                    (type) => (
                                                        <div
                                                            key={type.guid}
                                                            className="p-2 cursor-pointer text-[#181818] dark:text-[#FFFFFF] hover:bg-[#e1e1e1] dark:hover:bg-[#52525E]"
                                                            onMouseDown={(
                                                                e
                                                            ) => {
                                                                // onMouseDown срабатывает до onBlur, поэтому значение выбирается корректно
                                                                e.preventDefault() // Предотвращает потерю фокуса до обработки выбора
                                                                setField(
                                                                    'type_code',
                                                                    Number(
                                                                        type.code
                                                                    )
                                                                )
                                                                setTypeCodeName(
                                                                    type.name
                                                                )
                                                                setDropdownVisibleIndex(
                                                                    null
                                                                )
                                                            }}
                                                        >
                                                            {type.code} -{' '}
                                                            {type.name}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex w-fit h-full">
                                        <MoneyInput
                                            value={
                                                form.amount_of_budget_funds ?? 0
                                            }
                                            className={`form-input flex px-2 w-40 h-full rounded-md bg-[#181818] text-[#181818] dark:text-[#FFFFFF] ${errorClass(
                                                !!props.errors
                                                    ?.amount_of_budget_funds
                                            )}`}
                                            onChange={(newValue) => {
                                                const value = newValue
                                                setForm((prev) => ({
                                                    ...prev,
                                                    amount_of_budget_funds:
                                                        value,
                                                }))
                                            }}
                                            disabled={props.contractAction}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex flex-row text-right justify-end items-center gap-2">
                    <button
                        className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                        onClick={props.handleContractPositionEditPopup}
                    >
                        Отмена
                    </button>
                    {!props.contractAction && (
                        <button
                            className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                            onClick={() => {
                                props.onSave(form)
                            }}
                        >
                            Сохранить
                        </button>
                    )}
                </div>
            </div>

            {contractPositionCountryOriginPopup && (
                <ContractPositionCountryOriginPopup
                    theme={props.theme}
                    contractPositionGuid={props.contractPosition.guid ?? ''}
                    contractPosition={props.contractPosition}
                    handleContractPositionCountryOriginPopup={
                        handleContractPositionCountryOriginPopup
                    }
                    onSave={(country_of_origin) => {
                        setForm((prev) => ({
                            ...prev,
                            country_of_origin,
                        }))
                        handleContractPositionCountryOriginPopup()
                    }}
                />
            )}
            {contractPositionCountryManufacturerPopup && (
                <ContractPositionCountryManufacturerPopup
                    theme={props.theme}
                    contractPositionGuid={props.contractPosition.guid ?? ''}
                    contractPosition={props.contractPosition}
                    handleContractPositionCountryManufacturerPopup={
                        handleContractPositionCountryManufacturerPopup
                    }
                    onSave={(country_manufacturer) => {
                        setForm((prev) => ({
                            ...prev,
                            country_manufacturer,
                        }))
                        handleContractPositionCountryManufacturerPopup()
                    }}
                />
            )}
        </div>
    )
}
export default ContractPositionEditPopup
